import axios from "axios";
import { STORAGE_KEYS } from "@/constants";

/**
 * The app's single axios instance.
 *
 * `VUE_APP_BASEURL` is compile-time: CI injects it as a Docker `--build-arg`, which wins
 * over the .env files. It carries no path suffix — callers append `/jud/...` themselves.
 * Locally it is deliberately empty so every URL is relative and the devServer proxy in
 * `vue.config.js` forwards `/jud/*` to the backend without CORS.
 *
 * Note there is no Authorization header anywhere, by design: the JUD endpoints carry the
 * token in the request body (`jud_access_token`), so a request interceptor would have
 * nothing to attach.
 */
const http = axios.create({
  baseURL: process.env.VUE_APP_BASEURL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

/**
 * A 401 means the session is gone server-side; there is nothing left to recover, so wipe
 * local state and send the user back to the entry screen. The rejection is re-thrown so
 * the caller's own catch still runs and can show its message.
 */
http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error && error.response && error.response.status === 401) {
      localStorage.removeItem(STORAGE_KEYS.accessToken);
      localStorage.removeItem(STORAGE_KEYS.timer);
      localStorage.removeItem(STORAGE_KEYS.id);
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// --- Dev-only mock backend (offline end-to-end testing) ----------------------
// Enable with:  VUE_APP_MOCK=1 npm run serve
// Stubs the three live /jud endpoints so the whole UI flow can be walked to /complete
// without a real token or backend. Folded out of the production bundle by the guard.
if (process.env.NODE_ENV !== "production" && process.env.VUE_APP_MOCK === "1") {
  // Plain base64 (NOT base64url) — the JWT decode path calls atob() directly, which
  // rejects the url-safe '-'/'_' characters, so keep standard '+'/'/'/'=' output.
  const b64 = (obj) => btoa(JSON.stringify(obj));
  const fakeJwt = () => {
    const exp = Math.floor(Date.now() / 1000) + 3600;
    return (
      b64({ alg: "HS512", typ: "JWT" }) +
      "." +
      b64({ sub: "0000000000", auth: "ROLE_APP_USER", exp }) +
      ".mock-signature"
    );
  };
  const ok = (config, data) => ({
    data: { success: true, message: "MOCK OK", data },
    status: 200,
    statusText: "OK (mock)",
    headers: { "content-type": "application/json" },
    config,
    request: {},
  });
  const realAdapter = http.defaults.adapter || axios.defaults.adapter;
  http.defaults.adapter = (config) => {
    const url = String(config.url || "");
    const respond = (resp) => new Promise((resolve) => setTimeout(() => resolve(resp), 400));
    if (url.indexOf("/jud/check-token") !== -1) {
      return respond(ok(config, { id: "TEST-123456", accessToken: fakeJwt() }));
    }
    if (url.indexOf("/jud/select-challenge") !== -1) {
      return respond(ok(config, { challengeSequence: "توانا بود، هر که دانا بود" }));
    }
    if (url.indexOf("/jud/finalize") !== -1) {
      return respond(ok(config, { status: "COMPLETED", trackingId: "TEST-123456" }));
    }
    return realAdapter(config); // anything else -> real network
  };
  // eslint-disable-next-line no-console
  console.info("[http] MOCK backend enabled (VUE_APP_MOCK=1) — /jud/* endpoints are stubbed.");
}

export default http;
